"use client";

import { useState } from "react";
import logo from "../../assets/optimized/logo.webp";
import { FaLinkedin, FaFacebook } from "react-icons/fa";
import { FaSquareInstagram, FaTiktok } from "react-icons/fa6";
import { motion } from "framer-motion";
import useAuth from "../../services/useAuth";
import { Link, useLocation } from "react-router-dom";
import Footermain from "../../assets/optimized/footermain.webp";
import FootermainMobile from "../../assets/optimized/footermain-mobile.webp";
import ResponsiveImg from "../ui/ResponsiveImg";
import Modal from "./Modal";
import FAQs from "../../FooterPopUps/FAQs";
import BookingPolicy from "../../FooterPopUps/BookingPolicy";
import CancellationPolicy from "../../FooterPopUps/CancellationPolicy";
import PlanMyTrip from "../../FooterPopUps/PlanMyTrip";
import { toast } from "sonner";

// Routes where the section directly above the footer is the olive
// "A few peaks, mastered" panel and benefits from a fade-in.
// Add other routes here if they end with an olive section.
const ROUTES_WITH_OLIVE_FADE = ["/"];

const Footer = () => {
  const location = useLocation();
  const showOliveFade = ROUTES_WITH_OLIVE_FADE.includes(location.pathname);

  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");      // hidden — bots fill this, humans don't
  const [submitCount, setSubmitCount] = useState(0); // client-side rate limit
  const [lastSubmitTime, setLastSubmitTime] = useState(0);
  const [activeModal, setActiveModal] = useState(null);
  const closeModal = () => setActiveModal(null);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();

    // 1. Honeypot — if this has a value, it's a bot, silently bail
    if (honeypot) return;

    // 2. Client-side rate limit: max 3 attempts per 60s
    const now = Date.now();
    const withinWindow = now - lastSubmitTime < 60_000;
    if (withinWindow && submitCount >= 3) {
      toast.error("Too many attempts. Please wait a moment.");
      return;
    }

    // 3. Basic email validation
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmitCount((c) => (withinWindow ? c + 1 : 1));
    setLastSubmitTime(now);

    // TODO: wire to your newsletter API
    toast.success("You're on the list!");
    setEmail("");
  };

  return (
    <footer className="w-full bg-[#FCFCFC] flex flex-col">

      {/* 1. MOUNTAIN TRANSITION AREA */}
      <div className="w-full relative h-[450px] md:h-[650px] overflow-hidden">
        <ResponsiveImg
          src={Footermain}
          mobileSrc={FootermainMobile}
          alt="Himalayan Range"
          className="absolute inset-0 w-full h-full object-cover object-top z-10"
          loading="lazy"
          decoding="async"
        />

        {/* Top gradient fade — only shown on routes whose previous section is
            the olive "A few peaks, mastered" panel (currently just the homepage).
            Without this, on routes like /packages where the FAQ sits above the
            footer on a near-white background, the gradient reads as a green stripe. */}
        {showOliveFade && (
          <div className="absolute top-0 left-0 w-full h-48 bg-gradient-to-b from-[#575C3E] to-transparent z-20" />
        )}

        {/* Newsletter glass card */}
        <div className="absolute inset-0 z-30 flex items-end justify-center pb-16 px-6">
          <div className="bg-black/30 backdrop-blur-2xl p-10 md:p-14 rounded-[40px] border border-white/10 max-w-lg w-full text-center shadow-2xl">
            <h4 className="text-[#FCFCFC] text-3xl font-serif mb-3 drop-shadow-md">
              Stay in the Loop
            </h4>
            <p className="text-[#FCFCFC]/80 text-[10px] uppercase tracking-[0.3em] mb-10 popp">
              Insider tips & seasonal expedition updates.
            </p>

            <form
              onSubmit={handleNewsletterSubmit}
              className="flex items-center border-b border-white/30 pb-3 group transition-all focus-within:border-[#C99D89]"
            >
              {/* ── Honeypot: visually hidden, ignored by screen readers ── */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}
              />

              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="bg-transparent w-full outline-none text-sm text-[#FCFCFC] placeholder:text-white/40 popp"
              />
              <button
                type="submit"
                className="text-[#FCFCFC] uppercase text-[11px] font-bold tracking-[0.2em] hover:text-[#C99D89] transition-colors popp ml-4"
              >
                Join
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* 2. BOTTOM INFO AREA */}
      <div className="w-full bg-white pt-20 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-10 mb-16">
            <img src={logo} alt="logo" className="w-24 md:w-28 grayscale opacity-40" />

            <div className="flex flex-wrap justify-center gap-10 text-[10px] uppercase tracking-[0.3em] font-bold text-[#575C3E]/50 popp">
              <Link to="/" className="hover:text-[#C99D89] transition-colors">Home</Link>
              <Link to="/packages" className="hover:text-[#C99D89] transition-colors">Destinations</Link>
              <Link to="/login" className="hover:text-[#C99D89] transition-colors">Login</Link>
              <button type="button" onClick={() => setActiveModal("faqs")} className="hover:text-[#C99D89] transition-colors uppercase tracking-[0.3em] font-bold">FAQs</button>
              <button type="button" onClick={() => setActiveModal("booking")} className="hover:text-[#C99D89] transition-colors uppercase tracking-[0.3em] font-bold">Booking Policy</button>
              <button type="button" onClick={() => setActiveModal("cancellation")} className="hover:text-[#C99D89] transition-colors uppercase tracking-[0.3em] font-bold">Cancellation</button>
              <button type="button" onClick={() => setActiveModal("plan")} className="hover:text-[#C99D89] transition-colors uppercase tracking-[0.3em] font-bold">Plan My Trip</button>
            </div>

            <div className="flex items-center gap-8 text-[#575C3E]/30">
              <a href="https://www.instagram.com/himalayan.utopia?utm_source=qr&igsh=dnVzNWQ1bzBjdm5t" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[#C99D89] transition-all">
                <FaSquareInstagram size={20} />
              </a>
              <a href="https://www.tiktok.com/@himalayan.utopia?_r=1&_t=ZS-9378filxH40" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="hover:text-[#C99D89] transition-all">
                <FaTiktok size={18} />
              </a>
              <a href="https://www.facebook.com/share/17u77yaD8K" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-[#C99D89] transition-all">
                <FaFacebook size={20} />
              </a>
            </div>
          </div>

          <div className="w-full h-px bg-black/5 mb-10" />

          <div className="flex flex-col md:flex-row justify-between items-center opacity-40 text-[9px] uppercase tracking-[0.3em] font-bold text-[#575C3E] popp">
            <p>© 2026 Himalayan Utopia. All Rights Reserved.</p>
            <Link to="https://www.theinternetcompany.one" target="_blank" className="mt-4 md:mt-0 hover:opacity-70 transition">
              Designed & Developed by TIC
            </Link>
          </div>
        </div>
      </div>

      {/* Modals */}
      <Modal isOpen={activeModal === "faqs"} onClose={closeModal} title="Frequently Asked Questions">
        <FAQs />
      </Modal>
      <Modal isOpen={activeModal === "booking"} onClose={closeModal} title="Booking Policy">
        <BookingPolicy />
      </Modal>
      <Modal isOpen={activeModal === "cancellation"} onClose={closeModal} title="Cancellation Policy">
        <CancellationPolicy />
      </Modal>
      <Modal isOpen={activeModal === "plan"} onClose={closeModal} title="Plan Your Himalayan Trek" size="large">
        <PlanMyTrip />
      </Modal>
    </footer>
  );
};

export default Footer;